import React, { useCallback, useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import {
  getCameraOptionLabel,
  getVideoInputDevices,
  selectPreferredCameraDevice,
  TRUNG_THU_CAMERA_STORAGE_KEY,
} from "../../utils/trungThuCamera";
import {
  getJsQrInversionAttempts,
  getQrScanRegion,
} from "../../utils/trungThuQrScanner";

const SCANNER_BEEP_URL = `${process.env.PUBLIC_URL || ""}/scanner_beep.mp3`;
const QR_SCAN_THROTTLE_MS = 100;
const NATIVE_QR_JS_FALLBACK_INTERVAL = 3;

const stopMediaStream = (stream) => {
  stream?.getTracks().forEach((track) => track.stop());
};

const createQrBarcodeDetector = () => {
  if (typeof window.BarcodeDetector !== "function") return null;

  try {
    return new window.BarcodeDetector({ formats: ["qr_code"] });
  } catch {
    return null;
  }
};

const requestCameraStream = (deviceId = "") =>
  navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      ...(deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: { ideal: "environment" } }),
      width: { ideal: 720 },
      height: { ideal: 720 },
    },
  });

const applyOneXCameraZoom = async (stream) => {
  const videoTrack = stream?.getVideoTracks?.()[0];
  const capabilities = videoTrack?.getCapabilities?.();
  const zoomCapabilities = capabilities?.zoom;
  if (!zoomCapabilities || !videoTrack?.applyConstraints) return;

  const minZoom = Number(zoomCapabilities.min);
  const maxZoom = Number(zoomCapabilities.max);
  if (!Number.isFinite(minZoom) || !Number.isFinite(maxZoom)) return;

  const oneXZoom = Math.min(maxZoom, Math.max(minZoom, 1));
  try {
    await videoTrack.applyConstraints({ advanced: [{ zoom: oneXZoom }] });
  } catch {
    // Một số trình duyệt công bố zoom nhưng không cho áp dụng trên video stream.
  }
};

const getScanResult = (result) => {
  if (result === false) {
    return { success: false, message: "Không thể xử lý mã QR này." };
  }

  if (result && typeof result === "object") {
    return {
      success: result.success !== false,
      message: String(result.message || "").trim(),
    };
  }

  return { success: true, message: "" };
};

const TrungThuQrScannerModal = ({
  open,
  onClose,
  onScan,
  title = "Quét QR Code bánh trung thu",
}) => {
  const [scanQuantity, setScanQuantity] = useState("1");
  const [cameraError, setCameraError] = useState("");
  const [scanError, setScanError] = useState("");
  const [scanSuccessMessage, setScanSuccessMessage] = useState("");
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [scanLocked, setScanLocked] = useState(false);
  const [processing, setProcessing] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanVideoFrameCallbackRef = useRef(null);
  const scanFallbackTimeoutRef = useRef(null);
  const scanFrameProcessingRef = useRef(false);
  const scanLastAttemptAtRef = useRef(0);
  const nativeQrMissCountRef = useRef(0);
  const jsQrFailedAttemptsRef = useRef(0);
  const barcodeDetectorRef = useRef(undefined);
  const scanLockedRef = useRef(false);
  const processingRef = useRef(false);
  const processQrDataRef = useRef(null);
  const cameraRequestIdRef = useRef(0);
  const scannerBeepRef = useRef(null);
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const stopCamera = useCallback(() => {
    cameraRequestIdRef.current += 1;

    if (scanVideoFrameCallbackRef.current !== null) {
      videoRef.current?.cancelVideoFrameCallback?.(
        scanVideoFrameCallbackRef.current,
      );
      scanVideoFrameCallbackRef.current = null;
    }

    if (scanFallbackTimeoutRef.current !== null) {
      window.clearTimeout(scanFallbackTimeoutRef.current);
      scanFallbackTimeoutRef.current = null;
    }

    scanFrameProcessingRef.current = false;
    scanLastAttemptAtRef.current = 0;
    nativeQrMissCountRef.current = 0;
    jsQrFailedAttemptsRef.current = 0;

    stopMediaStream(streamRef.current);
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const unlockScanner = useCallback(() => {
    setScanQuantity("1");
    setScanSuccessMessage("");
    setScanError("");
    scanLockedRef.current = false;
    setScanLocked(false);
  }, []);

  const playScannerBeep = useCallback(() => {
    const scannerBeep = scannerBeepRef.current;
    if (!scannerBeep) return;

    scannerBeep.currentTime = 0;
    scannerBeep.play()?.catch(() => {
      // Trình duyệt có thể chặn âm thanh nếu người dùng chưa tương tác với trang.
    });
  }, []);

  const processQrData = useCallback(
    async (rawQrData) => {
      const qrData = String(rawQrData || "").trim();
      if (!qrData || scanLockedRef.current || processingRef.current) return;

      const quantity = Number(scanQuantity);
      if (!Number.isInteger(quantity) || quantity <= 0) {
        setScanError("Vui lòng nhập số lượng là số nguyên lớn hơn 0 trước khi quét.");
        return;
      }

      scanLockedRef.current = true;
      processingRef.current = true;
      setScanLocked(true);
      setProcessing(true);
      setScanError("");

      try {
        const result = getScanResult(await onScanRef.current?.(qrData, quantity));
        if (!result.success) {
          throw new Error(result.message || "Không thể xử lý mã QR này.");
        }

        setScanSuccessMessage(result.message || "Đã quét thành công");
        playScannerBeep();
      } catch (error) {
        setScanError(error.message || "Không thể xử lý mã QR. Vui lòng thử lại.");
      } finally {
        processingRef.current = false;
        setProcessing(false);
      }
    },
    [playScannerBeep, scanQuantity],
  );

  useEffect(() => {
    processQrDataRef.current = processQrData;
  }, [processQrData]);

  const scanQRFrame = useCallback(async () => {
    if (
      scanLockedRef.current ||
      processingRef.current ||
      scanFrameProcessingRef.current
    ) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== 4) return;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;

    scanFrameProcessingRef.current = true;
    const requestId = cameraRequestIdRef.current;

    try {
      const { sourceX, sourceY, sourceSize, analysisSize } = getQrScanRegion(
        width,
        height,
      );
      if (canvas.width !== analysisSize || canvas.height !== analysisSize) {
        canvas.width = analysisSize;
        canvas.height = analysisSize;
      }

      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;

      context.drawImage(
        video,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        analysisSize,
        analysisSize,
      );

      if (barcodeDetectorRef.current === undefined) {
        barcodeDetectorRef.current = createQrBarcodeDetector();
      }

      let qrData = "";
      let shouldRunJsQr = barcodeDetectorRef.current === null;

      if (barcodeDetectorRef.current) {
        try {
          const detectedBarcodes = await barcodeDetectorRef.current.detect(canvas);
          qrData = String(detectedBarcodes?.[0]?.rawValue || "").trim();

          if (qrData) {
            nativeQrMissCountRef.current = 0;
          } else {
            nativeQrMissCountRef.current += 1;
            shouldRunJsQr =
              nativeQrMissCountRef.current % NATIVE_QR_JS_FALLBACK_INTERVAL === 0;
          }
        } catch {
          barcodeDetectorRef.current = null;
          shouldRunJsQr = true;
        }
      }

      if (!qrData && shouldRunJsQr) {
        const imageData = context.getImageData(
          0,
          0,
          analysisSize,
          analysisSize,
        );
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: getJsQrInversionAttempts(
            jsQrFailedAttemptsRef.current,
          ),
        });
        qrData = String(code?.data || "").trim();

        if (qrData) {
          jsQrFailedAttemptsRef.current = 0;
        } else {
          jsQrFailedAttemptsRef.current += 1;
        }
      }

      if (qrData && requestId === cameraRequestIdRef.current) {
        nativeQrMissCountRef.current = 0;
        jsQrFailedAttemptsRef.current = 0;
        processQrDataRef.current?.(qrData);
      }
    } catch {
      // Bỏ qua frame lỗi và tiếp tục quét frame kế tiếp.
    } finally {
      scanFrameProcessingRef.current = false;
    }
  }, []);

  const scheduleNextScanFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return;

    if (typeof video.requestVideoFrameCallback === "function") {
      scanVideoFrameCallbackRef.current = video.requestVideoFrameCallback(
        (timestamp) => {
          scanVideoFrameCallbackRef.current = null;

          if (timestamp - scanLastAttemptAtRef.current >= QR_SCAN_THROTTLE_MS) {
            scanLastAttemptAtRef.current = timestamp;
            void scanQRFrame();
          }

          scheduleNextScanFrame();
        },
      );
      return;
    }

    scanFallbackTimeoutRef.current = window.setTimeout(() => {
      scanFallbackTimeoutRef.current = null;
      void scanQRFrame();
      scheduleNextScanFrame();
    }, QR_SCAN_THROTTLE_MS);
  }, [scanQRFrame]);

  const activateCameraStream = useCallback(
    async (stream, requestId) => {
      if (requestId !== cameraRequestIdRef.current) {
        stopMediaStream(stream);
        return false;
      }

      streamRef.current = stream;
      await applyOneXCameraZoom(stream);

      if (requestId !== cameraRequestIdRef.current) {
        stopMediaStream(stream);
        return false;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      barcodeDetectorRef.current = undefined;
      scanLastAttemptAtRef.current = 0;
      scheduleNextScanFrame();
      return true;
    },
    [scheduleNextScanFrame],
  );

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Trình duyệt không hỗ trợ camera.");
      return;
    }

    stopCamera();
    const requestId = cameraRequestIdRef.current;
    setCameraSwitching(true);

    let stream = null;
    try {
      setCameraError("");
      stream = await requestCameraStream();

      const currentDeviceId = String(
        stream.getVideoTracks()[0]?.getSettings?.().deviceId || "",
      );
      let videoDevices = [];

      if (navigator.mediaDevices.enumerateDevices) {
        try {
          videoDevices = getVideoInputDevices(
            await navigator.mediaDevices.enumerateDevices(),
          );
        } catch {
          videoDevices = [];
        }
      }

      if (requestId !== cameraRequestIdRef.current) {
        stopMediaStream(stream);
        return;
      }

      setCameraDevices(videoDevices);
      const savedDeviceId = String(
        localStorage.getItem(TRUNG_THU_CAMERA_STORAGE_KEY) || "",
      );
      const preferredCamera = selectPreferredCameraDevice(videoDevices, {
        savedDeviceId,
        currentDeviceId,
      });

      if (
        preferredCamera?.deviceId &&
        preferredCamera.deviceId !== currentDeviceId
      ) {
        stopMediaStream(stream);
        stream = null;

        try {
          stream = await requestCameraStream(preferredCamera.deviceId);
        } catch {
          if (preferredCamera.deviceId === savedDeviceId) {
            localStorage.removeItem(TRUNG_THU_CAMERA_STORAGE_KEY);
          }
          stream = await requestCameraStream();
        }
      }

      const activeDeviceId = String(
        stream.getVideoTracks()[0]?.getSettings?.().deviceId ||
          preferredCamera?.deviceId ||
          currentDeviceId,
      );
      const activated = await activateCameraStream(stream, requestId);
      if (activated) {
        setSelectedCameraId(activeDeviceId);
      }
    } catch {
      stopMediaStream(stream);
      setCameraError(
        "Không thể mở camera. Vui lòng kiểm tra quyền camera hoặc thử mở lại camera.",
      );
    } finally {
      if (requestId === cameraRequestIdRef.current) {
        setCameraSwitching(false);
      }
    }
  }, [activateCameraStream, stopCamera]);

  const handleCameraDeviceChange = useCallback(
    async (event) => {
      const nextDeviceId = event.target.value;
      if (
        !nextDeviceId ||
        nextDeviceId === selectedCameraId ||
        cameraSwitching
      ) {
        return;
      }

      stopCamera();
      const requestId = cameraRequestIdRef.current;
      setCameraSwitching(true);
      setCameraError("");

      let stream = null;
      try {
        stream = await requestCameraStream(nextDeviceId);
        const activeDeviceId = String(
          stream.getVideoTracks()[0]?.getSettings?.().deviceId || nextDeviceId,
        );
        const activated = await activateCameraStream(stream, requestId);
        if (!activated) return;

        setSelectedCameraId(activeDeviceId);
        localStorage.setItem(TRUNG_THU_CAMERA_STORAGE_KEY, activeDeviceId);
      } catch {
        stopMediaStream(stream);
        if (
          localStorage.getItem(TRUNG_THU_CAMERA_STORAGE_KEY) === nextDeviceId
        ) {
          localStorage.removeItem(TRUNG_THU_CAMERA_STORAGE_KEY);
        }

        try {
          stream = await requestCameraStream();
          const fallbackDeviceId = String(
            stream.getVideoTracks()[0]?.getSettings?.().deviceId || "",
          );
          const activated = await activateCameraStream(stream, requestId);
          if (activated) {
            setSelectedCameraId(fallbackDeviceId);
            setCameraError(
              "Không thể mở camera đã chọn. Hệ thống đã quay về camera sau mặc định.",
            );
          }
        } catch {
          stopMediaStream(stream);
          setCameraError(
            "Không thể chuyển camera. Vui lòng đóng và mở lại trình quét.",
          );
        }
      } finally {
        if (requestId === cameraRequestIdRef.current) {
          setCameraSwitching(false);
        }
      }
    },
    [activateCameraStream, cameraSwitching, selectedCameraId, stopCamera],
  );

  useEffect(() => {
    if (!open) {
      stopCamera();
      return undefined;
    }

    setScanQuantity("1");
    setCameraError("");
    setScanError("");
    setScanSuccessMessage("");
    scanLockedRef.current = false;
    processingRef.current = false;
    setScanLocked(false);
    setProcessing(false);

    const startTimer = window.setTimeout(() => {
      void startCamera();
    }, 150);

    return () => {
      window.clearTimeout(startTimer);
      stopCamera();
    };
  }, [open, startCamera, stopCamera]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose?.();
  }, [onClose, stopCamera]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "100%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <QrCodeScannerIcon color="primary" />
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <audio ref={scannerBeepRef} src={SCANNER_BEEP_URL} preload="auto" />

        <TextField
          autoFocus
          fullWidth
          required
          size="small"
          type="number"
          label="Số lượng"
          value={scanQuantity}
          onChange={(event) => {
            setScanQuantity(event.target.value);
            setScanError("");
          }}
          onClick={(event) => event.target.select()}
          onFocus={(event) => event.target.select()}
          inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
          helperText="Mỗi mã QR quét thành công sẽ cộng số lượng này."
          disabled={processing || scanLocked}
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "1 / 1",
            minHeight: 260,
            maxHeight: "55vh",
            bgcolor: "#171717",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Box component="canvas" ref={canvasRef} sx={{ display: "none" }} />
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: "16%",
              border: "3px solid rgba(25, 118, 210, 0.95)",
              borderRadius: 2,
              boxShadow:
                "0 0 0 999px rgba(0, 0, 0, 0.3), 0 0 18px rgba(25, 118, 210, 0.5)",
              pointerEvents: "none",
            }}
          />

          {cameraDevices.length > 1 && (
            <FormControl
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                minWidth: 180,
                bgcolor: "rgba(255, 255, 255, 0.94)",
                borderRadius: 1,
              }}
            >
              <InputLabel id="trung-thu-camera-label">Camera</InputLabel>
              <Select
                labelId="trung-thu-camera-label"
                value={selectedCameraId}
                label="Camera"
                onChange={handleCameraDeviceChange}
                disabled={cameraSwitching || processing}
              >
                {cameraDevices.map((device, index) => (
                  <MenuItem key={device.deviceId} value={device.deviceId}>
                    {getCameraOptionLabel(device, index)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(cameraSwitching || processing) && (
            <Box
              role="status"
              sx={{
                position: "absolute",
                left: "50%",
                bottom: 16,
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                color: "white",
                bgcolor: "rgba(0, 0, 0, 0.72)",
                borderRadius: 5,
                whiteSpace: "nowrap",
              }}
            >
              <CircularProgress size={18} color="inherit" />
              {cameraSwitching ? "Đang mở camera..." : "Đang xử lý..."}
            </Box>
          )}

          {scanLocked && scanSuccessMessage && !processing && (
            <Box
              role="status"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                px: 3,
                color: "white",
                textAlign: "center",
                bgcolor: "rgba(0, 0, 0, 0.68)",
              }}
            >
              <QrCodeScannerIcon sx={{ fontSize: 48, color: "#66bb6a" }} />
              <Typography sx={{ fontWeight: 700 }}>{scanSuccessMessage}</Typography>
              <Button variant="contained" color="success" onClick={unlockScanner}>
                Quét tiếp
              </Button>
            </Box>
          )}
        </Box>

        {cameraError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {cameraError}
          </Alert>
        )}
        {scanError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={unlockScanner}>
                Thử lại
              </Button>
            }
            sx={{ mt: 2 }}
          >
            {scanError}
          </Alert>
        )}

      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<CameraAltIcon />}
          onClick={() => void startCamera()}
          disabled={cameraSwitching || processing || scanLocked}
        >
          Mở lại camera
        </Button>
        <Button onClick={handleClose} color="inherit">
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrungThuQrScannerModal;
